import { createContext, useContext, useEffect, useMemo, useState, type ReactNode, type ReactElement } from "react";
import type { UserManager, User } from "oidc-client-ts";
import { createUserManager } from "./oidc.js";
import type { RuntimeConfig } from "../types.js";

interface AuthState {
  user: User | null;
  loading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /** The API rejected the token: try one silent refresh, else drop the session (shows Sign in). */
  sessionExpired: () => Promise<void>;
  accessToken: string | null;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ config, children }: { config: RuntimeConfig; children: ReactNode }): ReactElement {
  const manager: UserManager = useMemo(() => createUserManager(config), [config]);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function renewOrForget(): Promise<User | null> {
    try {
      return (await manager.signinSilent()) ?? null;
    } catch {
      await manager.removeUser().catch(() => undefined);
      return null;
    }
  }

  useEffect(() => {
    let active = true;
    async function bootstrap(): Promise<void> {
      try {
        if (window.location.search.includes("code=") && window.location.search.includes("state=")) {
          const u = await manager.signinRedirectCallback();
          if (active) setUser(u);
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          let u = await manager.getUser();
          // A stored session whose tokens have expired: refresh silently with the refresh token if
          // possible, otherwise forget it so the user sees Sign in (never call the API with it).
          if (u?.expired) u = await renewOrForget();
          if (active) setUser(u);
        }
      } catch {
        // A failed callback/refresh must not leave the app on the spinner.
        await manager.removeUser().catch(() => undefined);
        if (active) setUser(null);
      } finally {
        if (active) setLoading(false);
      }
    }
    void bootstrap();
    const onLoaded = (u: User): void => setUser(u);
    const onUnloaded = (): void => setUser(null);
    manager.events.addUserLoaded(onLoaded);
    manager.events.addUserUnloaded(onUnloaded);
    return () => {
      active = false;
      manager.events.removeUserLoaded(onLoaded);
      manager.events.removeUserUnloaded(onUnloaded);
    };
  }, [manager]);

  const value: AuthState = {
    user,
    loading,
    // The API Gateway Cognito authorizer (no OAuth scopes configured) validates the ID token, and the
    // portal Lambda reads `email` and `cognito:groups`, which only the ID token carries.
    accessToken: user?.id_token ?? null,
    login: () => manager.signinRedirect(),
    logout: () => manager.signoutRedirect(),
    sessionExpired: async () => {
      setUser(await renewOrForget());
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
